import React, { useState, useEffect } from 'react';
import { Event, formatDateForDisplay, formatDateForInput } from '../lib/supabase';
import './Calendar.css';
import './EventDetail.css';

interface EventDetailProps {
  event: Event;
  onUpdateEvent: (id: string, updates: Partial<Event>) => Promise<void>;
  onDeleteEvent: (id: string) => Promise<void>;
  onClose: () => void;
  initialIsEditing?: boolean;
}

const EventDetail: React.FC<EventDetailProps> = ({ event, onUpdateEvent, onDeleteEvent, onClose, initialIsEditing = false }) => {
  const [isEditing, setIsEditing] = useState(initialIsEditing);
  const [editFormData, setEditFormData] = useState<Partial<Event>>(
    initialIsEditing ? {
      title: event.title || '',
      start_date: formatDateForInput(event.start_date),
      end_date: event.end_date ? formatDateForInput(event.end_date) : '',
      location: event.location || '',
      priority: event.priority || undefined,
      partnership_angle: event.partnership_angle || '',
      description: event.description || '',
      notes: event.notes || '',
      owner: event.owner || '',
    } : {}
  );
  const [showSuccess, setShowSuccess] = useState(false);
  const [currentEvent, setCurrentEvent] = useState<Event>(event);
  
  // Update currentEvent when event prop changes (after save)
  useEffect(() => {
    setCurrentEvent(event);
  }, [event]);
  
  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  
  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isEditing) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose, isEditing]);

  // Reset edit form when event changes
  React.useEffect(() => {
    if (!isEditing) {
      setEditFormData({});
    }
  }, [currentEvent.id, isEditing]);
  
  // Reset success message when switching to edit mode
  React.useEffect(() => {
    if (isEditing) {
      setShowSuccess(false);
    }
  }, [isEditing]);


  const getPriorityColor = (priority?: string): string => {
    if (!priority) return '';
    const lowerPriority = priority.toLowerCase();
    if (lowerPriority === 'high') return 'priority-high';
    if (lowerPriority === 'medium') return 'priority-medium';
    if (lowerPriority === 'low') return 'priority-low';
    return '';
  };
  
  const handlePriorityChange = async (newPriority: string) => {
    if (!currentEvent.id) return;
    const priorityValue = newPriority !== '' ? newPriority as 'High' | 'Medium' | 'Low' : undefined;
    try {
      await onUpdateEvent(currentEvent.id, { priority: priorityValue });
      setCurrentEvent(prev => ({ ...prev, priority: priorityValue }));
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error updating priority:', error);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditFormData({
      title: currentEvent.title || '',
      start_date: formatDateForInput(currentEvent.start_date),
      end_date: currentEvent.end_date ? formatDateForInput(currentEvent.end_date) : '',
      location: currentEvent.location || '',
      priority: currentEvent.priority || undefined,
      partnership_angle: currentEvent.partnership_angle || '',
      description: currentEvent.description || '',
      notes: currentEvent.notes || '',
      owner: currentEvent.owner || '',
    });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData({});
  };

  const handleSaveEdit = async () => {
    if (!currentEvent.id) return;
    
    try {
      const updates: Partial<Event> = {
        title: editFormData.title !== undefined ? editFormData.title : currentEvent.title,
        start_date: editFormData.start_date !== undefined ? editFormData.start_date : currentEvent.start_date,
      };

      // Handle optional fields - only include if changed, use undefined for empty values
      if (editFormData.end_date !== undefined) {
        updates.end_date = editFormData.end_date !== '' ? editFormData.end_date : undefined;
      }
      if (editFormData.location !== undefined) {
        updates.location = editFormData.location !== '' ? editFormData.location : undefined;
      }
      if (editFormData.priority !== undefined) {
        const validPriorities: ('High' | 'Medium' | 'Low')[] = ['High', 'Medium', 'Low'];
        updates.priority = editFormData.priority && validPriorities.includes(editFormData.priority as 'High' | 'Medium' | 'Low') 
          ? editFormData.priority as 'High' | 'Medium' | 'Low' 
          : undefined;
      }
      if (editFormData.partnership_angle !== undefined) {
        updates.partnership_angle = editFormData.partnership_angle !== '' ? editFormData.partnership_angle : undefined;
      }
      if (editFormData.description !== undefined) {
        updates.description = editFormData.description !== '' ? editFormData.description : undefined;
      }
      // Always include notes if it exists in editFormData (user may have cleared it)
      if ('notes' in editFormData) {
        updates.notes = editFormData.notes && editFormData.notes !== '' ? editFormData.notes : undefined;
      }
      if (editFormData.owner !== undefined) {
        updates.owner = editFormData.owner !== '' ? editFormData.owner : undefined;
      }

      // Update the event and wait for it to complete
      await onUpdateEvent(currentEvent.id, updates);
      
      // Update local state with the saved changes immediately (merge updates into current event)
      const updatedEvent: Event = {
        ...currentEvent,
        ...updates,
        // Ensure required fields are preserved
        title: updates.title ?? currentEvent.title,
        start_date: updates.start_date ?? currentEvent.start_date,
      };
      setCurrentEvent(updatedEvent);
      
      setEditFormData({});
      setIsEditing(false);
      // Show success message after switching to view mode
      setTimeout(() => {
        setShowSuccess(true);
        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 3000);
      }, 100);
    } catch (error) {
      console.error('Error saving event:', error);
      // Error is already handled by onUpdateEvent, so we don't need to show another alert
    }
  };
  
  const handleDelete = async () => {
    if (!currentEvent.id) return;
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    await onDeleteEvent(currentEvent.id);
    onClose();
  };

  const handleInputChange = (field: keyof Event, value: string) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="event-modal-overlay" onClick={(e) => {
      // Close modal when clicking outside (same as clicking X)
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="event-modal" onClick={(e) => e.stopPropagation()}>
        <button 
          className="event-modal-close" 
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>
        <div className="card">
          <div className="event-modal-content-wrapper">
            {showSuccess && (
              <div className="save-success-message">
                ✓ Changes saved successfully
              </div>
            )}
            {isEditing ? (
              <>
                <h3>Edit Event</h3>
              <div className="event-modal-form">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={editFormData.title || ''}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    value={editFormData.start_date || ''}
                    onChange={(e) => handleInputChange('start_date', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={editFormData.end_date || ''}
                    onChange={(e) => handleInputChange('end_date', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    value={editFormData.location || ''}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    className="form-input"
                    placeholder="e.g., Las Vegas"
                  />
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={editFormData.priority || ''}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select priority</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Owner</label>
                <input
                  type="text"
                  value={editFormData.owner || ''}
                  onChange={(e) => handleInputChange('owner', e.target.value)}
                  className="form-input"
                  placeholder="e.g., John Doe"
                />
              </div>

              <div className="form-group">
                <label>Partnership Angle</label>
                <input
                  type="text"
                  value={editFormData.partnership_angle || ''}
                  onChange={(e) => handleInputChange('partnership_angle', e.target.value)}
                  className="form-input"
                  placeholder="Partnership focus"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={editFormData.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="form-input"
                  rows={3}
                  placeholder="Event description"
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={editFormData.notes || ''}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  className="form-input"
                  rows={4}
                  placeholder="Additional notes and details"
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <h3>{currentEvent.title}</h3>
            <div className="event-modal-content">
              <div className="event-modal-row">
                <strong>Date:</strong>
                <span>{formatDateForDisplay(currentEvent.start_date)}</span>
                {currentEvent.end_date && currentEvent.end_date !== currentEvent.start_date && (
                  <span> - {formatDateForDisplay(currentEvent.end_date)}</span>
                )}
              </div>
              {currentEvent.location && (
                <div className="event-modal-row">
                  <strong>Location:</strong>
                  <span>{currentEvent.location}</span>
                </div>
              )}
              {currentEvent.owner && (
                <div className="event-modal-row">
                  <strong>Owner:</strong>
                  <span>{currentEvent.owner}</span>
                </div>
              )}
              <div className="event-modal-row">
                <strong>Priority:</strong>
                <select
                  value={currentEvent.priority || ''}
                  onChange={(e) => handlePriorityChange(e.target.value)}
                  className={`priority-dropdown ${getPriorityColor(currentEvent.priority)}`}
                >
                  <option value="">Select priority</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
              {currentEvent.partnership_angle && (
                <div className="event-modal-row">
                  <strong>Partnership Angle:</strong>
                  <span>{currentEvent.partnership_angle}</span>
                </div>
              )}
              {currentEvent.description && (
                <div className="event-modal-row">
                  <strong>Description:</strong>
                  <p>{currentEvent.description}</p>
                </div>
              )}
              {currentEvent.notes && (
                <div className="event-modal-row">
                  <strong>Notes:</strong>
                  <p>{currentEvent.notes}</p>
                </div>
              )}
            </div>
          </>
        )}
          </div>
          {isEditing ? (
            <div className="form-actions">
              <button className="btn btn-secondary" onClick={handleCancelEdit}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveEdit}>
                Save Changes
              </button>
            </div>
          ) : (
            <div className="form-actions">
              <button className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
              <button className="btn btn-primary" onClick={handleEditClick}>
                Edit
              </button>
              <button className="btn btn-danger" onClick={handleDelete}>
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventDetail;

